import React, { useEffect, useState } from "react";
import { Button, Card, Form, Input, Modal, Popconfirm, Table, message, Space } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { fetchData, postData, deleteData, showError } from "../../utils";
import { useBlockScreen, useRoot } from "../../rootContext";

const PlatformManagement = () => {
  const [platforms, setPlatforms] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const blocker = useBlockScreen();
  const root = useRoot();

  const loadPlatforms = async () => {
    try {
      const resp = await fetchData(`${window.SiteBasePath}/api/platform`, { blocker, root });
      setPlatforms(resp || []);
    } catch (err) {
      showError("Failed to load platforms");
    }
  };

  useEffect(() => {
    loadPlatforms();
  }, []);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await postData(`${window.SiteBasePath}/api/platform/${editing.Id}`, values, { blocker, root });
        message.success("Platform updated");
      } else {
        await postData(`${window.SiteBasePath}/api/platform`, values, { blocker, root });
        message.success("Platform added");
      }
      setModalVisible(false);
      form.resetFields();
      setEditing(null);
      loadPlatforms();
    } catch (err) {
      showError(err?.message || "Failed to save platform");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteData(`${window.SiteBasePath}/api/platform/${id}`, { blocker, root });
      message.success("Platform deleted");
      loadPlatforms();
    } catch (err) {
      showError("Failed to delete platform");
    }
  };

  const columns = [
    { title: "ID", dataIndex: "Id", key: "Id", width: 80 },
    { title: "Name", dataIndex: "Name", key: "Name" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => {
              setEditing(record);
              form.setFieldsValue(record);
              setModalVisible(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this platform?"
            onConfirm={() => handleDelete(record.Id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Manage Platforms" extra={
      <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
        Add Platform
      </Button>
    }>
      <Table dataSource={platforms} columns={columns} rowKey="Id" />

      <Modal
        title={editing ? "Edit Platform" : "Add Platform"}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditing(null); form.resetFields(); }}
        onOk={handleSave}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="Name" label="Platform Name" rules={[{ required: true, message: "Please enter platform name" }]}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default PlatformManagement;
